import React, { useState, useEffect, useRef } from 'react';

// Deploy status queries the API that matches current domain
// - karporat.dalartservice.com → api-karporat.dalartservice.com (Cloudflare)
// - ru-karporat.dalartservice.com → same-origin /api/ (nginx proxies to backend)
const getDeployUrl = () => {
  const host = window.location.hostname;
  if (host.startsWith('ru-')) {
    return '/api/health/deploy';
  }
  return 'https://api-karporat.dalartservice.com/api/health/deploy';
};

const DeployStatusIndicator = () => {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch(getDeployUrl());
      const json = await res.json();
      setData(json);
      setStatus(json.status || 'unknown');
    } catch {
      setStatus('unknown');
      setData(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 30000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const s = (status || '').toUpperCase();

  const green = s === 'OK';
  const yellow = s === 'DEPLOYING';
  const red = s === 'FAIL';

  // Always return a stable wrapper div to avoid React insertBefore DOM errors
  if (status === null) return <div />;

  const details = data
    ? `${data.commit ? `commit: ${data.commit.slice(0, 7)}` : ''}${data.time ? ` | ${data.time}` : ''}${data.duration ? ` (${data.duration})` : ''}`
    : '';

  // During DEPLOYING — show warning banner
  if (yellow) {
    return (
      <div className="bg-yellow-50 border-b border-yellow-200">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-300" title="Успешно задеплоено" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400 animate-pulse shadow-sm shadow-yellow-200" title="Идёт деплой" />
            <div className="w-2.5 h-2.5 rounded-full bg-gray-300" title="Деплой упал" />
          </div>
          <span className="text-sm font-medium text-yellow-800">
            Идёт обновление системы — подождите 1-2 минуты, не сохраняйте данные
          </span>
        </div>
      </div>
    );
  }

  // OK or FAIL — compact bar
  return (
    <div className={`border-b ${red ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 py-1 flex items-center justify-center gap-3">
        <div className="flex items-center gap-1.5">
          <div
            title={green ? `Деплой успешен\n${data?.reason || ''}${details ? `\n${details}` : ''}` : 'Успешно задеплоено — неактивно'}
            className={`w-2.5 h-2.5 rounded-full transition-colors cursor-help ${
              green ? 'bg-green-500 shadow-sm shadow-green-300' : 'bg-gray-300'
            }`}
          />
          <div
            title="Идёт деплой — неактивно"
            className="w-2.5 h-2.5 rounded-full bg-gray-300 cursor-help"
          />
          <div
            title={red ? `Деплой упал\n${data?.reason || ''}${details ? `\n${details}` : ''}` : 'Деплой упал — неактивно'}
            className={`w-2.5 h-2.5 rounded-full transition-colors cursor-help ${
              red ? 'bg-red-500 shadow-sm shadow-red-300' : 'bg-gray-300'
            }`}
          />
        </div>
        <span className={`text-xs ${red ? 'text-red-700' : 'text-gray-500'}`}>
          {red ? `Деплой упал: ${data?.reason || 'ошибка'}` : `Деплой OK`}
          {details ? ` — ${details}` : ''}
        </span>
      </div>
    </div>
  );
};

export default DeployStatusIndicator;
